import * as amplitude from '@amplitude/analytics-browser';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { telemetry } from '../backend';
import captureSpecificError from '../capture-error';
import { getStore } from '../store';
import { selectHasCurrentMemberActiveSubscription } from '../store/modules/backend-service';
import { getOnTheWaterFromState } from '../store/modules/signal';

let amplitudeInitialised = false;

const buildAdditionalInformation = async () => {
  const state = getStore().getState();
  const { currentMember } = state.backendService;

  const clientOnTheWaterState = getOnTheWaterFromState(state);

  // eslint-disable-next-line no-undef

  const searchParams = {};
  if (window.appHistory) {
    const urlParams = new URLSearchParams(window.appHistory?.location?.search);

    urlParams.forEach((v, k) => {
      searchParams[`qs_${k}`] = v;
    });
  }

  const info = await Device.getInfo();
  const subscribed = selectHasCurrentMemberActiveSubscription(state);

  return {
    model: info.model,
    platform: info.platform ?? 'Browser',
    version: info.osVersion,
    device: info.name,
    manufacturer: info.manufacturer,
    isVirtual: info.isVirtual, // Not sure if we want this? Would be VERY rare if its virtual
    environment: Capacitor.isNativePlatform() ? 'capacitor' : 'browser',
    /* eslint-enable no-undef */
    isLoggedIn: !!currentMember,
    clientOnTheWaterState,
    subscribed,
    ...searchParams,
  };
};

export const captureEvent = async ({ type, payload, sendToAmplitude }) => {
  const additionalInformation = await buildAdditionalInformation();
  const { userProperties, ...rest } = payload;
  if (amplitudeInitialised && sendToAmplitude) {
    amplitude.track(type, { ...additionalInformation, ...rest });

    if (userProperties) {
      userProperties.forEach((property) => {
        switch (property.type) {
          case 'append':
            amplitude.identify(
              new amplitude.Identify().append(property.name, [property.value]),
            );
            break;
          case 'increment':
            amplitude.identify(new amplitude.Identify().add(property.name, 1));
            break;
          case 'decrement':
            amplitude.identify(new amplitude.Identify().add(property.name, -1));
            break;
          case 'set':
          default:
            amplitude.identify(
              new amplitude.Identify().set(property.name, property.value),
            );
            break;
        }
      });
    }
  }
};

/**
 * Format user properties if changing, ensure changes are reflected in each implementation
 * @param {array} watercrafts - User property
 * @param {array} interests - User property
 * @param {array} trips - User property
 * @param {array} floatPlans - User property
 */
const identifyWithUserProperties = ({
  watercrafts,
  interests,
  trips,
  floatPlans,
  otherInterest,
}) => {
  const userInterests = interests?.map((interest) => interest.name) ?? [];
  if (otherInterest) {
    userInterests.push(otherInterest);
  }

  const state = getStore().getState();
  const subscribed = selectHasCurrentMemberActiveSubscription(state);

  const identify = new amplitude.Identify();

  identify.set('interests', userInterests);
  identify.set('tripCount', trips?.length ?? undefined);
  identify.set('floatPlanCount', floatPlans?.length ?? undefined);
  identify.set('watercraftCount', watercrafts?.length ?? undefined);
  identify.set('subscribed', subscribed);

  const defaultStartupPageVariant =
    state.appControls.memberSettings?.experimentsConfig?.defaultStartupPage
      ?.variant;
  if (defaultStartupPageVariant) {
    identify.set('defaultStartupPageVariant', defaultStartupPageVariant);
  }

  return identify;
};

let identifyRetryQueue = [];

const identifyAmplitude = (identity, queueOnFail) => {
  if (amplitudeInitialised) {
    if (identity) {
      const { id } = identity;
      amplitude.setUserId(id);
      // Set user properties
      const amplitudeIdentify = identifyWithUserProperties(identity);
      amplitude.identify(amplitudeIdentify);
    } else {
      amplitude.setUserId(null);
    }
  } else if (queueOnFail) {
    identifyRetryQueue.push(identity);
  }
};

export const identify = (identity) => identifyAmplitude(identity, true);

export const initialize = async () => {
  const amplitudeApiKey = window.DECKEE_CONFIG?.amplitude?.apiKey ?? null;
  if (amplitudeApiKey) {
    amplitude.init(amplitudeApiKey, {
      autocapture: false,
      minIdLength: 1,
    });
    amplitudeInitialised = true;

    // Now that we're initialised, identify any queued identities.

    identifyRetryQueue?.forEach((identity) =>
      identifyAmplitude(identity, false),
    );
    identifyRetryQueue = [];
  } else {
    const notificationMessage =
      "[amplitude.initialise] failed initialise amplitude: couldn't retrieve api key";
    /* eslint-disable no-bitwise */
    telemetry(
      telemetry.WARN | telemetry.NOTIFY_SYSTEM_CHANNEL,
      notificationMessage,
    );
    /* eslint-enable no-bitwise */
    captureSpecificError(
      notificationMessage,
      'containers/app-main-client/src/services/event-capture/amplitude.js',
    );
  }
};
